import { useEffect } from 'react';
import { X } from 'lucide-react';

const TERMS_CONTENT = [
  {
    title: '1. Acceptance of These Terms',
    body: 'These Terms of Use ("Terms") govern your access to and use of tolvai.com and the products and services made available through it (collectively, the "Services"), offered by Tolvai Company LLC ("Tolvai," "we," "us," or "our"). By accessing or using the Services, you agree to these Terms and our Privacy Policy. If you do not agree, do not use the Services.',
  },
  {
    title: '2. The Services',
    body: "Tolvai develops and provides software products, as described on the website. We may add, change, or discontinue features of the Services at any time, and we'll use reasonable efforts to notify you of material changes or discontinuations where practical.",
  },
  {
    title: '3. Eligibility',
    body: 'You must be of the age of majority in your jurisdiction to use the Services on your own behalf, or have the consent and supervision of a parent or legal guardian. By using the Services, you represent that you meet this requirement and that you have the legal capacity to enter into these Terms.',
  },
  {
    title: '4. Accounts and Security',
    body: 'If the Services require an account, you are responsible for maintaining the confidentiality of your login credentials and for all activity under your account. Notify us promptly of any unauthorized use. We are not responsible for losses caused by your failure to safeguard your credentials.',
  },
  {
    title: '5. Acceptable Use',
    prefix: 'You agree not to use the Services to:',
    list: [
      'Violate any applicable law or the rights of others, including intellectual property rights',
      'Transmit malicious code or attempt to interfere with the proper functioning of the Services',
      'Engage in fraudulent, deceptive, or misleading conduct',
      'Reverse-engineer, decompile, or attempt to derive the source code of the Services, except where applicable law permits',
      'Scrape, harvest, or collect data from the Services except as we permit',
      'Impersonate any person or misrepresent your affiliation with Tolvai',
    ],
  },
  {
    title: '6. Intellectual Property',
    body: 'We and our licensors own all right, title, and interest in the Services, including all related intellectual property. Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Services for their intended purpose. You retain ownership of any content you submit through the Services ("Your Content"), and you grant us a license to use it as needed to provide the Services. You may not use our trademarks, logos, or trade names without our prior written consent.',
  },
  {
    title: '7. Third-Party Content and Services',
    body: 'The Services may link to, or integrate with, third-party content, websites, or services that we do not control. Your use of third-party content or services is at your own risk and subject to their own terms; we are not responsible for them.',
  },
  {
    title: '8. Feedback',
    body: 'If you provide us with suggestions or feedback about the Services, we may use it without restriction or compensation to you.',
  },
  {
    title: '9. Disclaimers',
    body: 'THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT, EXCEPT TO THE EXTENT SUCH WARRANTIES CANNOT BE EXCLUDED UNDER APPLICABLE LAW.',
  },
  {
    title: '10. Limitation of Liability',
    body: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, TOLVAI WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUE, ARISING FROM YOUR USE OF THE SERVICES.',
  },
  {
    title: '11. Indemnification',
    body: "You agree to defend, indemnify, and hold Tolvai harmless from claims, damages, and expenses (including reasonable attorneys' fees) arising from your breach of these Terms or your misuse of the Services.",
  },
  {
    title: '12. Term and Termination',
    body: 'These Terms remain in effect while you use the Services. We may suspend or terminate your access at any time for a violation of these Terms or for any other reason, with notice where practical. You may stop using the Services at any time. Provisions that by their nature should survive termination (e.g., intellectual property, disclaimers, limitation of liability, indemnification) will survive.',
  },
  {
    title: '13. Governing Law and Dispute Resolution',
    body: 'These Terms, and any dispute arising out of or relating to them or your use of the Services, are governed by the laws of the State of Wyoming, without regard to its conflict of laws principles. Any dispute that cannot be resolved informally will be brought exclusively in the state or federal courts located in Wyoming, and you and Tolvai each consent to the personal jurisdiction of those courts. This section does not limit any statutory rights you may have as a consumer under the law of your country of residence.',
  },
  {
    title: '14. Changes to These Terms',
    body: 'We may modify these Terms at any time. We will post the updated Terms with a new "Last Updated" date, and material changes will be notified through the Services or by other reasonable means. Continued use of the Services after a change takes effect means you accept the updated Terms.',
  },
  {
    title: '15. Miscellaneous',
    body: 'These Terms, together with our Privacy Policy and any other referenced policies, are the entire agreement between you and Tolvai regarding the Services. If any provision is found unenforceable, the rest remain in effect. Our failure to enforce a provision is not a waiver of our right to do so later. You may not assign these Terms without our consent; we may assign them freely, including in connection with a merger, acquisition, or sale of assets. Neither party is liable for delays or failures caused by events beyond its reasonable control.',
  },
  {
    title: '16. Contact Us',
    body: 'You can reach us at legal@tolvai.com.',
  },
];

export default function TermsModal({ isOpen, onClose }) {
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="Terms-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Terms of Use">
      <div className="Terms-modal" onClick={(e) => e.stopPropagation()}>
        <div className="Terms-modal-header">
          <div>
            <span className="Terms-modal-eyebrow">Tolvai Company LLC</span>
            <h2 className="Terms-modal-title">Terms of Use</h2>
            <p className="Terms-modal-date">Effective: October, 2026</p>
          </div>
          <button className="Terms-close" onClick={onClose} aria-label="Close">
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        <div className="Terms-modal-body">
          {TERMS_CONTENT.map((section) => (
            <div key={section.title} className="Terms-section">
              <h3 className="Terms-section-title">{section.title}</h3>
              {section.prefix && <p className="Terms-section-text">{section.prefix}</p>}
              {section.list && (
                <ul className="Terms-section-list">
                  {section.list.map((item) => <li key={item}>{item}</li>)}
                </ul>
              )}
              {section.body && <p className="Terms-section-text">{section.body}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}